import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseModal } from '../ui/modal';
import { useAuth } from '../../context/AuthContext';
import { usePlan } from '../../hooks/usePlan';
import {
  FiZap, FiEdit3, FiMic, FiMicOff, FiImage, FiX, FiLoader, FiCheck,
  FiAlertTriangle, FiArrowRight, FiPlus, FiChevronDown, FiChevronUp, FiFileText
} from 'react-icons/fi';

const API = import.meta.env.VITE_API_URL;

interface Maestro { id: number; nombre: string }
interface TarifaOption extends Maestro {
  precio: number;
  iva_porcentaje: number;
  retencion_porcentaje: number;
  aplicar_iva?: number;
  aplicar_retencion?: number;
}

interface QuickDefaults {
  cliente: Maestro | null;
  hora_inicio: string | null;
  idioma_id: number | null;
  tipo_visita_id: number | null;
  lugar_inicio: string | null;
  lugares_frecuentes: string[];
  tarifa: TarifaOption | null;
  proxima_referencia: string;
}

interface FormState {
  referencia: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  pax: string;
  cliente_id: string;      // id numérico o '__nuevo__'
  cliente_nuevo: string;   // nombre del cliente a crear
  idioma_id: string;
  tipo_visita_id: string;    // id numérico o '__nuevo__'
  tipo_visita_nuevo: string; // nombre del tipo de visita a crear
  lugar_inicio: string;
  tarifa_id: string;
  precio: string;
  observaciones: string;
}

type Confidences = Partial<Record<keyof FormState, number>>;

interface QuickAddServiceProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (servicioId: number) => void;
  /** Render inline (sin modal) — usado por el onboarding */
  embedded?: boolean;
  /** Texto precargado en la pestaña de captura */
  initialText?: string;
}

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = (): FormState => ({
  referencia: '', fecha: hoyISO(), hora_inicio: '', hora_fin: '', pax: '',
  cliente_id: '', cliente_nuevo: '', idioma_id: '', tipo_visita_id: '',
  tipo_visita_nuevo: '', lugar_inicio: '', tarifa_id: '', precio: '', observaciones: ''
});

const EJEMPLO_WHATSAPP =
  'Hola! Te confirmo grupo mañana a las 10h, 25 pax franceses, visita panorámica por el centro. ' +
  'Punto de encuentro: Hotel Riu Plaza España. Tarifa habitual 180€. Ref RSV-4411 — Viajes Sol';

const QuickAddService: React.FC<QuickAddServiceProps> = ({
  isOpen, onClose, onCreated, embedded = false, initialText = ''
}) => {
  const { token } = useAuth();
  const { canUseAiCapture, usage, isPro, refreshPlan } = usePlan();
  const navigate = useNavigate();

  const [tab, setTab] = useState<'captura' | 'manual'>('captura');
  const [phase, setPhase] = useState<'input' | 'review' | 'done'>('input');
  const [text, setText] = useState(initialText);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const [form, setForm] = useState<FormState>(emptyForm());
  const [confidences, setConfidences] = useState<Confidences>({});
  const [capturaId, setCapturaId] = useState<number | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [createdClienteId, setCreatedClienteId] = useState<number | null>(null);

  const [defaults, setDefaults] = useState<QuickDefaults | null>(null);
  const [clientes, setClientes] = useState<Maestro[]>([]);
  const [idiomas, setIdiomas] = useState<Maestro[]>([]);
  const [tiposVisita, setTiposVisita] = useState<Maestro[]>([]);
  const [tarifas, setTarifas] = useState<TarifaOption[]>([]);

  const recognitionRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => () => { recognitionRef.current?.stop?.(); }, []);

  // ---- Carga de maestros y defaults al abrir ----
  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const [dRes, cRes, iRes, tvRes, tRes] = await Promise.all([
          fetch(`${API}/api/servicios/quick-defaults`, { headers: authHeaders }),
          fetch(`${API}/api/clientes`, { headers: authHeaders }),
          fetch(`${API}/api/idiomas`, { headers: authHeaders }),
          fetch(`${API}/api/tipos_visita`, { headers: authHeaders }),
          fetch(`${API}/api/tarifas`, { headers: authHeaders })
        ]);
        if (cancelled) return;
        const d = dRes.ok ? (await dRes.json()).data as QuickDefaults : null;
        const cl = cRes.ok ? (await cRes.json()).data || [] : [];
        const id = iRes.ok ? (await iRes.json()).data || [] : [];
        const tv = tvRes.ok ? (await tvRes.json()).data || [] : [];
        const tf = tRes.ok ? (await tRes.json()).data || [] : [];
        setDefaults(d);
        setClientes(cl.filter((c: any) => !c.is_demo));
        setIdiomas(id);
        setTiposVisita(tv);
        setTarifas(tf.filter((t: any) => !t.is_demo && t.activo !== 0));
        // Prefijar defaults en el formulario manual
        setForm(prev => ({
          ...prev,
          referencia: prev.referencia || d?.proxima_referencia || '',
          hora_inicio: prev.hora_inicio || d?.hora_inicio?.slice(0, 5) || '',
          cliente_id: prev.cliente_id || (d?.cliente ? String(d.cliente.id) : ''),
          idioma_id: prev.idioma_id || (d?.idioma_id ? String(d.idioma_id) : ''),
          tipo_visita_id: prev.tipo_visita_id || (d?.tipo_visita_id ? String(d.tipo_visita_id) : ''),
          lugar_inicio: prev.lugar_inicio || d?.lugar_inicio || '',
          tarifa_id: prev.tarifa_id || (d?.tarifa ? String(d.tarifa.id) : ''),
          precio: prev.precio || (d?.tarifa ? String(d.tarifa.precio) : '')
        }));
      } catch { /* sin red: el formulario funciona vacío */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  // ---- Dictado (Web Speech API) ----
  const toggleDictation = () => {
    const SR = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SR) { setError('Tu navegador no soporta dictado por voz. Prueba en Chrome.'); return; }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = 'es-ES';
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      const chunk = Array.from(e.results).slice(e.resultIndex)
        .map((r: any) => r[0].transcript).join(' ');
      setText(prev => (prev ? prev + ' ' : '') + chunk.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  // ---- Imagen ----
  const handleImage = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Solo se admiten imágenes.'); return; }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- Extracción IA ----
  const extract = async () => {
    if (!text.trim() && !image) { setError('Pega el mensaje de la agencia o añade una foto.'); return; }
    setExtracting(true);
    setError('');
    setLimitReached(false);
    try {
      let res: Response;
      if (image) {
        const fd = new FormData();
        fd.append('image', image);
        if (text.trim()) fd.append('text', text.trim());
        res = await fetch(`${API}/api/servicios/quick-capture`, { method: 'POST', headers: authHeaders, body: fd });
      } else {
        res = await fetch(`${API}/api/servicios/quick-capture`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim() })
        });
      }
      const body = await res.json();
      if (!res.ok) {
        if (body.code === 'PLAN_LIMIT_REACHED') { setLimitReached(true); return; }
        throw new Error(body.message || 'No se pudo extraer el servicio');
      }
      const ex = body.data.extraccion;
      setCapturaId(body.data.captura_id);

      const conf: Confidences = {};
      const next: FormState = {
        ...emptyForm(),
        referencia: defaults?.proxima_referencia || '',
        fecha: ex.fecha.value || hoyISO(),
        hora_inicio: ex.hora_inicio.value || defaults?.hora_inicio?.slice(0, 5) || '',
        hora_fin: ex.hora_fin.value || '',
        pax: ex.pax.value != null ? String(ex.pax.value) : '',
        idioma_id: ex.idioma.match_id ? String(ex.idioma.match_id) : '',
        tipo_visita_id: ex.tipo_visita.match_id
          ? String(ex.tipo_visita.match_id)
          : (ex.tipo_visita.raw ? '__nuevo__' : ''),
        tipo_visita_nuevo: !ex.tipo_visita.match_id && ex.tipo_visita.raw ? ex.tipo_visita.raw : '',
        lugar_inicio: ex.lugar_inicio.value || '',
        cliente_id: ex.cliente.match_id ? String(ex.cliente.match_id) : (ex.cliente.es_nuevo && ex.cliente.raw ? '__nuevo__' : ''),
        cliente_nuevo: ex.cliente.es_nuevo && ex.cliente.raw ? ex.cliente.raw : '',
        tarifa_id: ex.tarifa.match_id ? String(ex.tarifa.match_id) : (defaults?.tarifa ? String(defaults.tarifa.id) : ''),
        precio: ex.tarifa.precio_detectado != null
          ? String(ex.tarifa.precio_detectado)
          : (ex.tarifa.match_id
            ? String(tarifas.find(t => t.id === ex.tarifa.match_id)?.precio ?? '')
            : (defaults?.tarifa ? String(defaults.tarifa.precio) : '')),
        observaciones: [
          ex.referencia_externa.value ? `Ref. agencia: ${ex.referencia_externa.value}` : null,
          ex.observaciones
        ].filter(Boolean).join('. ')
      };
      conf.fecha = ex.fecha.confidence;
      conf.hora_inicio = ex.hora_inicio.confidence;
      conf.pax = ex.pax.confidence;
      conf.idioma_id = ex.idioma.confidence;
      conf.tipo_visita_id = ex.tipo_visita.confidence;
      conf.lugar_inicio = ex.lugar_inicio.confidence;
      conf.cliente_id = ex.cliente.confidence;
      conf.tarifa_id = ex.tarifa.confidence;

      setForm(next);
      setConfidences(conf);
      setPhase('review');
    } catch (e: any) {
      setError(e.message || 'Error procesando la captura');
    } finally {
      setExtracting(false);
    }
  };

  // ---- Crear servicio ----
  const createServicio = async () => {
    if (!form.fecha) { setError('La fecha es obligatoria.'); return; }
    setSaving(true);
    setError('');
    try {
      // 1. Cliente nuevo si procede
      let clienteId: number | null = form.cliente_id && form.cliente_id !== '__nuevo__' ? Number(form.cliente_id) : null;
      if (form.cliente_id === '__nuevo__' && form.cliente_nuevo.trim()) {
        const cRes = await fetch(`${API}/api/clientes`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'agencia', nombre: form.cliente_nuevo.trim(), email_facturacion: '', nif: '' })
        });
        const cBody = await cRes.json();
        if (!cRes.ok) throw new Error(cBody.message || 'No se pudo crear el cliente');
        clienteId = cBody.id;
      }

      // 1b. Tipo de visita nuevo si procede (la IA extrajo uno que no existe aún)
      let tipoVisitaId: number | null = form.tipo_visita_id && form.tipo_visita_id !== '__nuevo__' ? Number(form.tipo_visita_id) : null;
      if (form.tipo_visita_id === '__nuevo__' && form.tipo_visita_nuevo.trim()) {
        const tvRes = await fetch(`${API}/api/tipos_visita`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nombre: form.tipo_visita_nuevo.trim() })
        });
        const tvBody = await tvRes.json();
        if (!tvRes.ok) throw new Error(tvBody.message || 'No se pudo crear el tipo de visita');
        tipoVisitaId = tvBody.id;
      }

      // 2. Tarifa -> línea de servicio_tarifas
      const tarifaSel = tarifas.find(t => String(t.id) === form.tarifa_id) || defaults?.tarifa || null;
      const base = Number(form.precio) || Number(tarifaSel?.precio) || 0;
      const iva = tarifaSel ? Number(tarifaSel.iva_porcentaje) || 0 : 21;
      const ret = tarifaSel ? Number(tarifaSel.retencion_porcentaje) || 0 : 0;
      const tarifasPayload = base > 0 ? [{
        tarifa_id: tarifaSel?.id ?? null,
        descripcion: tarifaSel?.nombre || 'Servicio de guía',
        cantidad: 1,
        base_imponible: base,
        iva_porcentaje: iva,
        retencion_porcentaje: ret,
        importe_iva: +(base * iva / 100).toFixed(2),
        importe_retencion: +(base * ret / 100).toFixed(2)
      }] : [];

      // 3. Crear servicio
      const sRes = await fetch(`${API}/api/servicios`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referencia: form.referencia || defaults?.proxima_referencia || `SRV-${Date.now()}`,
          fecha: form.fecha,
          hora_inicio: form.hora_inicio || null,
          hora_fin: form.hora_fin || null,
          pax: form.pax ? Number(form.pax) : null,
          cliente_id: clienteId,
          idioma_id: form.idioma_id ? Number(form.idioma_id) : null,
          tipo_visita_id: tipoVisitaId,
          lugar_inicio: form.lugar_inicio || null,
          observaciones: form.observaciones || null,
          tipo: 'propio',
          status: 'in_progress',
          tarifas: tarifasPayload
        })
      });
      const sBody = await sRes.json();
      if (!sRes.ok) throw new Error(sBody.error || sBody.message || 'No se pudo crear el servicio');
      const servicioId = sBody.data.id;

      // 4. Vincular captura (métricas de precisión)
      if (capturaId) {
        fetch(`${API}/api/servicios/quick-capture/${capturaId}`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ servicio_id: servicioId, status: 'confirmada' })
        }).catch(() => {});
      }

      setCreatedId(servicioId);
      setCreatedClienteId(clienteId);
      setPhase('done');
      refreshPlan();
      onCreated?.(servicioId);
    } catch (e: any) {
      setError(e.message || 'Error al crear el servicio');
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setPhase('input');
    setText('');
    clearImage();
    setForm(emptyForm());
    setConfidences({});
    setCapturaId(null);
    setCreatedId(null);
    setError('');
  };

  const handleClose = () => {
    if (listening) recognitionRef.current?.stop();
    resetAll();
    onClose();
  };

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const isLow = (k: keyof FormState) => {
    const c = confidences[k];
    return phase === 'review' && c !== undefined && c < 0.7;
  };

  const inputCls = (k?: keyof FormState) =>
    `w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white ` +
    `focus:outline-none focus:ring-2 focus:ring-blue-500 ` +
    (k && isLow(k)
      ? 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/10'
      : 'border-gray-300 dark:border-gray-700');

  const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  const capturesLeft = usage?.aiCapturesRemaining ?? -1;

  // ================= RENDER =================

  const renderTabs = () => (
    <div className="flex rounded-lg bg-gray-100 dark:bg-gray-900 p-1 gap-1">
      <button
        onClick={() => { setTab('captura'); setPhase('input'); setError(''); }}
        className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          tab === 'captura'
            ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        <FiZap className="w-4 h-4" /> Pegar / dictar
      </button>
      <button
        onClick={() => { setTab('manual'); setPhase('input'); setError(''); setConfidences({}); }}
        className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          tab === 'manual'
            ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        <FiEdit3 className="w-4 h-4" /> Manual en 15s
      </button>
    </div>
  );

  const renderCapturaInput = () => (
    <div className="space-y-3">
      <div className="relative">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={5}
          placeholder={'Pega aquí el WhatsApp de la agencia…\n\n"Grupo mañana 10h, 25 pax franceses, salida Hotel Riu, 180€ — Viajes Sol"'}
          className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
        <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5">
          <button
            onClick={toggleDictation}
            title={listening ? 'Detener dictado' : 'Dictar'}
            className={`p-2 rounded-lg transition-colors ${
              listening
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 animate-pulse'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
          >
            {listening ? <FiMicOff className="w-4 h-4" /> : <FiMic className="w-4 h-4" />}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Foto de la hoja de servicio"
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <FiImage className="w-4 h-4" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => handleImage(e.target.files?.[0] || null)} />
        </div>
      </div>

      {imagePreview && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
          <img src={imagePreview} alt="Adjunto" className="h-12 w-12 rounded object-cover" />
          <span className="text-sm text-gray-600 dark:text-gray-300 flex-1 truncate">{image?.name}</span>
          <button onClick={clearImage} className="p-1.5 text-gray-400 hover:text-red-500">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {!text && !image && (
        <button
          onClick={() => setText(EJEMPLO_WHATSAPP)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          Probar con un ejemplo
        </button>
      )}

      {limitReached && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
          Has usado tus {usage?.aiCapturesThisMonth ?? 10} capturas IA del mes.{' '}
          <button onClick={() => navigate('/suscripcion')} className="font-semibold underline">
            Pasa a Pro
          </button>{' '}
          para capturas ilimitadas, o usa la pestaña Manual.
        </div>
      )}

      <button
        onClick={extract}
        disabled={extracting || (!text.trim() && !image)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 text-sm transition-colors"
      >
        {extracting ? (<><FiLoader className="w-4 h-4 animate-spin" /> Leyendo el mensaje…</>) : (<><FiZap className="w-4 h-4" /> Crear servicio con IA</>)}
      </button>

      {!isPro && capturesLeft >= 0 && (
        <p className="text-center text-xs text-gray-400 dark:text-gray-500">
          {capturesLeft} capturas IA restantes este mes
        </p>
      )}
    </div>
  );

  const renderForm = () => (
    <div className="space-y-3">
      {phase === 'review' && (
        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
          <FiCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Revisa los datos extraídos. Los campos en <span className="font-semibold text-amber-600 dark:text-amber-400">ámbar</span> necesitan tu confirmación.</span>
        </div>
      )}

      {form.cliente_id === '__nuevo__' && form.cliente_nuevo && (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
          <FiPlus className="w-4 h-4 shrink-0" />
          Cliente nuevo: se creará «{form.cliente_nuevo}»
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Fecha *</label>
          <input type="date" value={form.fecha} onChange={set('fecha')} className={inputCls('fecha')} />
        </div>
        <div>
          <label className={labelCls}>Hora inicio</label>
          <input type="time" value={form.hora_inicio} onChange={set('hora_inicio')} className={inputCls('hora_inicio')} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Cliente / agencia</label>
          <select value={form.cliente_id} onChange={set('cliente_id')} className={inputCls('cliente_id')}>
            <option value="">— Sin cliente —</option>
            {form.cliente_nuevo && <option value="__nuevo__">➕ Crear «{form.cliente_nuevo}»</option>}
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Pax</label>
          <input type="number" min="1" value={form.pax} onChange={set('pax')} placeholder="25" className={inputCls('pax')} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Idioma</label>
          <select value={form.idioma_id} onChange={set('idioma_id')} className={inputCls('idioma_id')}>
            <option value="">—</option>
            {idiomas.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipo de visita</label>
          <select value={form.tipo_visita_id} onChange={set('tipo_visita_id')} className={inputCls('tipo_visita_id')}>
            <option value="">—</option>
            {form.tipo_visita_nuevo && <option value="__nuevo__">➕ Crear «{form.tipo_visita_nuevo}»</option>}
            {tiposVisita.map(tv => <option key={tv.id} value={tv.id}>{tv.nombre}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Punto de encuentro</label>
        <input
          type="text" value={form.lugar_inicio} onChange={set('lugar_inicio')}
          list="qas-lugares" placeholder="Ej. Puerta del Sol" className={inputCls('lugar_inicio')}
        />
        <datalist id="qas-lugares">
          {(defaults?.lugares_frecuentes || []).map(l => <option key={l} value={l} />)}
        </datalist>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {/* Sin tarifas creadas (usuario nuevo): el select solo mete ruido; basta el importe */}
        {tarifas.length > 0 && (
          <div className="col-span-2">
            <label className={labelCls}>Tarifa</label>
            <select
              value={form.tarifa_id}
              onChange={e => {
                const t = tarifas.find(x => String(x.id) === e.target.value);
                setForm(prev => ({ ...prev, tarifa_id: e.target.value, precio: t ? String(t.precio) : prev.precio }));
              }}
              className={inputCls('tarifa_id')}
            >
              <option value="">—</option>
              {tarifas.map(t => <option key={t.id} value={t.id}>{t.nombre} ({Number(t.precio).toFixed(2)}€)</option>)}
            </select>
          </div>
        )}
        <div className={tarifas.length > 0 ? '' : 'col-span-3'}>
          <label className={labelCls}>Importe (€)</label>
          <input type="number" min="0" step="0.01" value={form.precio} onChange={set('precio')} className={inputCls('tarifa_id')} />
        </div>
      </div>

      <button
        onClick={() => setShowMore(v => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
      >
        {showMore ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
        Más opciones
      </button>

      {showMore && (
        <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Referencia</label>
              <input type="text" value={form.referencia} onChange={set('referencia')} className={inputCls()} />
            </div>
            <div>
              <label className={labelCls}>Hora fin</label>
              <input type="time" value={form.hora_fin} onChange={set('hora_fin')} className={inputCls()} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Observaciones</label>
            <textarea rows={2} value={form.observaciones} onChange={set('observaciones')} className={inputCls()} />
          </div>
          <button
            onClick={() => { handleClose(); navigate('/servicios/nuevo'); }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Necesito el modo avanzado (todos los campos) →
          </button>
        </div>
      )}

      <button
        onClick={createServicio}
        disabled={saving || !form.fecha}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 text-sm transition-colors"
      >
        {saving ? (<><FiLoader className="w-4 h-4 animate-spin" /> Creando…</>) : (<><FiCheck className="w-4 h-4" /> Crear servicio</>)}
      </button>
    </div>
  );

  const renderDone = () => (
    <div className="py-6 text-center space-y-4">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <FiCheck className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Servicio creado</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Ya está en tu agenda. ¿La convertimos en factura?
        </p>
      </div>
      <div className="space-y-2">
        <button
          onClick={() => {
            handleClose();
            navigate(`/facturacion/new?servicios=${createdId}${createdClienteId ? `&cliente_id=${createdClienteId}` : ''}`);
          }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 text-sm transition-colors"
        >
          <FiFileText className="w-4 h-4" /> Convertir en factura <FiArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={resetAll}
          className="w-full rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium py-2.5 text-sm transition-colors"
        >
          Crear otro servicio
        </button>
      </div>
    </div>
  );

  const content = (
    <div className="space-y-4">
      {phase !== 'done' && (
        <>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nuevo servicio</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Pega el mensaje de la agencia y deja que la IA lo rellene, o hazlo a mano en 15 segundos.
            </p>
          </div>
          {renderTabs()}
        </>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
          <FiAlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {phase === 'done'
        ? renderDone()
        : tab === 'manual' || phase === 'review'
          ? renderForm()
          : canUseAiCapture || limitReached
            ? renderCapturaInput()
            : renderCapturaInput()}
    </div>
  );

  if (embedded) {
    return isOpen ? (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        {content}
      </div>
    ) : null;
  }

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} size="lg">
      {content}
    </BaseModal>
  );
};

export default QuickAddService;
